import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Users, Mail, MessageSquare, Heart, ArrowUpRight, DollarSign, Sparkles, Wand2, Clock, X, Timer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const Stat = ({ icon: Icon, label, value, sub, color = "#3A5A4A", testid }) => (
  <div className="hc-card p-5" data-testid={testid}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wider text-[#7A736E]">{label}</p>
        <p className="font-display text-3xl mt-1 truncate" style={{ color }}>{value}</p>
        {sub && <p className="text-xs text-[#7A736E] mt-1">{sub}</p>}
      </div>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}15` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
    </div>
  </div>
);

export default function AdminDashboardTab() {
  const [stats, setStats] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [trial, setTrial] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [showBackfillConfirm, setShowBackfillConfirm] = useState(false);
  const [showCountdownConfirm, setShowCountdownConfirm] = useState(false);
  const [countdownBusy, setCountdownBusy] = useState(false);
  const [trialBusy, setTrialBusy] = useState(false);

  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => { /* noop */ });
  const loadCountdown = () => api.get("/admin/founding/countdown-status").then((r) => setCountdown(r.data)).catch(() => setCountdown({}));
  const loadTrial = () => api.get("/admin/trial/stats").then((r) => setTrial(r.data)).catch(() => setTrial({}));
  useEffect(() => { loadStats(); loadCountdown(); loadTrial(); }, []);

  const runTrialExpiry = async () => {
    if (!window.confirm("Run trial expiry job now? Users whose 14-day trial has ended will be downgraded to Free and emailed.")) return;
    setTrialBusy(true);
    try {
      const { data } = await api.post("/admin/trial/run-expiry-now", { live: true });
      toast.success(`Reverted ${data.reverted} expired trial${data.reverted === 1 ? "" : "s"}.`);
      loadTrial();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Job failed");
    } finally {
      setTrialBusy(false);
    }
  };

  const runTrialBackfill = async () => {
    if (!window.confirm("Grant a 14-day Premium trial to all existing Free users who never had one?\n\nThis will run a DRY-RUN first, then ask for confirmation.")) return;
    setTrialBusy(true);
    try {
      const dry = await api.post("/admin/trial/backfill", { live: false });
      const count = dry.data.count || 0;
      if (count === 0) {
        toast.info("No eligible users to backfill.");
        return;
      }
      if (!window.confirm(`Grant 14-day trial to ${count} existing free user${count === 1 ? "" : "s"}? This cannot be undone.`)) return;
      const live = await api.post("/admin/trial/backfill", { live: true });
      toast.success(`Granted 14-day trial to ${live.data.granted} user${live.data.granted === 1 ? "" : "s"}.`);
      loadTrial();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill failed");
    } finally {
      setTrialBusy(false);
    }
  };

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/founding/backfill", { confirm: true });
      if (data.granted === 0) {
        toast.info(data.message || "No eligible users to backfill.");
      } else {
        toast.success(`Granted Founder status to ${data.granted} existing paid subscriber${data.granted === 1 ? "" : "s"}.`);
      }
      setShowBackfillConfirm(false);
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  const startCountdown = async () => {
    setCountdownBusy(true);
    try {
      const { data } = await api.post("/admin/founding/start-countdown", { days: 7 });
      toast.success(`7-day countdown started. Closes ${new Date(data.closes_at).toLocaleString()}.`);
      setShowCountdownConfirm(false);
      loadCountdown();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start countdown");
    } finally {
      setCountdownBusy(false);
    }
  };

  const cancelCountdown = async () => {
    if (!window.confirm("Cancel the active 7-day countdown? Future 7d/3d/1d emails will stop.")) return;
    setCountdownBusy(true);
    try {
      await api.post("/admin/founding/cancel-countdown");
      toast.success("Countdown cancelled.");
      loadCountdown();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cancel failed");
    } finally {
      setCountdownBusy(false);
    }
  };

  if (!stats) return <div className="p-8 text-[#7A736E]">Loading stats…</div>;
  const { users, feedback_open, chests_total, emails, founding_members } = stats;
  const mrr = (users.paid * 14.99).toFixed(0);
  const eligibleForBackfill = (users.paid || 0) - (founding_members?.taken || 0);
  const countdownClosesAt = countdown?.closes_at ? new Date(countdown.closes_at) : null;
  const countdownActive = countdownClosesAt && countdownClosesAt > new Date();
  const daysLeft = countdownActive ? Math.max(1, Math.ceil((countdownClosesAt - new Date()) / 86400000)) : null;

  return (
    <div className="space-y-6" data-testid="admin-dashboard-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Total subscribers" value={users.total} sub={`+${users.new_this_week} this week`} testid="stat-total-users" />
        <Stat icon={Heart} label="Paid subscribers" value={users.paid} sub={`+${users.new_paid_this_week} this week`} color="#C87B67" testid="stat-paid-users" />
        <Stat icon={DollarSign} label="Estimated MRR" value={`$${mrr}`} sub={`${users.paid} × $14.99/mo (approx.)`} color="#D9A441" testid="stat-mrr" />
        <Stat icon={MessageSquare} label="Open feedback" value={feedback_open} sub={feedback_open ? "needs your attention" : "all caught up"} color="#7A736E" testid="stat-feedback" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat icon={Sparkles} label="Founding Members" value={`${founding_members?.taken ?? 0} / ${founding_members?.total ?? 100}`} sub={`${founding_members?.remaining ?? 100} spots remaining`} color="#3A5A4A" testid="stat-founding-members" />
        <Stat icon={Mail} label="Emails sent (week)" value={emails.week} sub={`${emails.total.toLocaleString()} all-time`} testid="stat-emails-week" />
        <Stat icon={ArrowUpRight} label="Open rate (week)" value={`${emails.open_rate_week.toFixed(0)}%`} sub={`${emails.opens_week} opens`} color="#3A5A4A" testid="stat-open-rate" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Stat icon={Heart} label="Total Hope Chests" value={chests_total} sub="across all users" color="#C87B67" testid="stat-chests" />
        <div className="hc-card p-5 bg-gradient-to-br from-[#3A5A4A] to-[#2C4538] text-white">
          <p className="text-xs uppercase tracking-wider text-[#E8DCC8]">Founding Member offer</p>
          {countdownActive ? (
            <p className="font-display text-2xl mt-1 flex items-baseline gap-2" data-testid="countdown-active">
              <Clock className="w-5 h-5 text-[#E8DCC8]" />
              {daysLeft === 1 ? "Closes tomorrow" : `Closes in ${daysLeft} days`}
            </p>
          ) : (
            <p className="font-display text-2xl mt-1">
              {founding_members?.remaining ?? 100} of {founding_members?.total ?? 100} spots open
            </p>
          )}
          <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full bg-[#E8DCC8] transition-all"
              style={{ width: `${Math.min(100, ((founding_members?.taken ?? 0) / (founding_members?.total ?? 100)) * 100)}%` }}
            />
          </div>
          <p className="text-xs text-white/70 mt-2">
            {founding_members?.taken ?? 0} founders · next would be #{(founding_members?.taken ?? 0) + 1}
          </p>

          {/* Backfill button */}
          {eligibleForBackfill > 0 && (
            <Button
              onClick={() => setShowBackfillConfirm(true)}
              disabled={backfilling}
              className="mt-4 w-full rounded-full bg-[#E8DCC8] hover:bg-white text-[#2C4538] font-semibold text-sm"
              data-testid="founding-backfill-button"
            >
              <Wand2 className="w-4 h-4 mr-1.5" />
              Grant founder status to {eligibleForBackfill} existing paid user{eligibleForBackfill === 1 ? "" : "s"}
            </Button>
          )}

          {/* Countdown control */}
          {!countdownActive ? (
            <Button
              onClick={() => setShowCountdownConfirm(true)}
              disabled={countdownBusy}
              variant="outline"
              className="mt-2 w-full rounded-full bg-transparent border border-white/30 hover:bg-white/10 text-[#E8DCC8] text-sm"
              data-testid="founding-start-countdown"
            >
              <Clock className="w-4 h-4 mr-1.5" />
              Start 7-day countdown
            </Button>
          ) : (
            <div className="mt-3 space-y-2">
              <div className="flex items-center justify-between text-xs text-white/80">
                <span>
                  📨 Sent so far: 7d:{countdown?.sends_by_day?.["7d"] ?? 0} · 3d:{countdown?.sends_by_day?.["3d"] ?? 0} · 1d:{countdown?.sends_by_day?.["1d"] ?? 0}
                </span>
              </div>
              <Button
                onClick={cancelCountdown}
                disabled={countdownBusy}
                variant="outline"
                className="w-full rounded-full bg-transparent border border-white/30 hover:bg-white/10 text-white text-xs"
                data-testid="founding-cancel-countdown"
              >
                <X className="w-3 h-3 mr-1.5" />
                Cancel countdown
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Countdown confirmation modal */}
      {showCountdownConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowCountdownConfirm(false)} data-testid="countdown-modal">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#3A5A4A] flex items-center justify-center">
                <Clock className="w-5 h-5 text-[#E8DCC8]" />
              </div>
              <h3 className="font-display text-xl">Start 7-day countdown?</h3>
            </div>
            <p className="text-sm text-[#7A736E] leading-relaxed mb-2">
              The Founding Member offer will close 7 days from now. The pricing page banner switches to "Closes in N days" + "You'd be Founder #N".
            </p>
            <p className="text-sm text-[#7A736E] leading-relaxed mb-2">
              Free users (who signed up before today) will automatically receive 3 reminder emails:
            </p>
            <ul className="text-sm text-[#7A736E] leading-relaxed mb-4 pl-5 list-disc">
              <li>Day 1: "7 days left"</li>
              <li>Day 4: "3 days left"</li>
              <li>Day 6: "Last day"</li>
            </ul>
            <p className="text-sm text-[#7A736E] leading-relaxed mb-5">
              You can cancel the countdown anytime before it ends.
            </p>
            <div className="flex gap-2 justify-end">
              <Button onClick={() => setShowCountdownConfirm(false)} disabled={countdownBusy} variant="outline" className="rounded-full border-[#EBE5DF]" data-testid="countdown-cancel-modal">
                Not yet
              </Button>
              <Button onClick={startCountdown} disabled={countdownBusy} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538] text-white" data-testid="countdown-confirm">
                {countdownBusy ? "Starting…" : "Yes, start the countdown"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Backfill confirmation modal */}
      {showBackfillConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowBackfillConfirm(false)} data-testid="backfill-modal">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#3A5A4A] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#E8DCC8]" />
              </div>
              <h3 className="font-display text-xl">Grant Founder status to {eligibleForBackfill} paid user{eligibleForBackfill === 1 ? "" : "s"}?</h3>
            </div>
            <p className="text-sm text-[#7A736E] leading-relaxed mb-2">
              This will grant Founding Member status to your existing paid subscribers in the order they upgraded
              (oldest first). They'll get their founder number, badge, and price-lock-for-life promise.
            </p>
            <p className="text-sm text-[#7A736E] leading-relaxed mb-5">
              This is a one-way action — once granted, you can't easily ungrant it. Idempotent: re-running it does nothing extra.
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                onClick={() => setShowBackfillConfirm(false)}
                disabled={backfilling}
                variant="outline"
                className="rounded-full border-[#EBE5DF]"
                data-testid="backfill-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={runBackfill}
                disabled={backfilling}
                className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538] text-white"
                data-testid="backfill-confirm"
              >
                {backfilling ? "Granting…" : `Yes, grant ${eligibleForBackfill} founder${eligibleForBackfill === 1 ? "" : "s"}`}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="hc-card p-6" data-testid="trial-admin-card">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#E8DCC8] flex items-center justify-center">
              <Timer className="w-5 h-5 text-[#3A5A4A]" />
            </div>
            <div>
              <h3 className="font-display text-lg">14-day Premium trial</h3>
              <p className="text-xs text-[#7A736E]">Every new signup is auto-granted 14 days. Daily cron at 04:00 UTC downgrades expired trials.</p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-[#7A736E]">Active</p>
            <p className="font-display text-2xl mt-1" data-testid="trial-stat-active">{trial?.active ?? "—"}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-[#7A736E]">≤ 3 days left</p>
            <p className="font-display text-2xl mt-1" style={{ color: "#C87B67" }} data-testid="trial-stat-expiring">{trial?.expiring_within_3_days ?? "—"}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-[#7A736E]">Converted</p>
            <p className="font-display text-2xl mt-1" style={{ color: "#3A5A4A" }} data-testid="trial-stat-converted">{trial?.converted_to_paid ?? "—"}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-[#7A736E]">Expired → Free</p>
            <p className="font-display text-2xl mt-1 text-[#7A736E]" data-testid="trial-stat-expired">{trial?.expired_to_free ?? "—"}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-[#7A736E]">All-time starts</p>
            <p className="font-display text-2xl mt-1 text-[#7A736E]" data-testid="trial-stat-total">{trial?.total_trials_started ?? "—"}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={runTrialExpiry} disabled={trialBusy} variant="outline" className="rounded-full border-[#EBE5DF] text-sm" data-testid="trial-run-expiry-button">
            <Clock className="w-4 h-4 mr-1.5" />
            {trialBusy ? "Working…" : "Run expiry job now"}
          </Button>
          <Button onClick={runTrialBackfill} disabled={trialBusy} variant="outline" className="rounded-full border-[#EBE5DF] text-sm" data-testid="trial-backfill-button">
            <Wand2 className="w-4 h-4 mr-1.5" />
            Backfill existing free users
          </Button>
        </div>
      </div>

      <div className="hc-card p-6">
        <h3 className="font-display text-lg mb-2">Welcome back, Michael</h3>
        <p className="text-sm text-[#7A736E] leading-relaxed">
          Use the tabs on the left to manage subscribers, edit emails, triage feedback, and update the roadmap.
          The numbers above refresh every page load — your sources of truth for what&apos;s actually happening.
        </p>
        <ul className="mt-4 text-sm text-[#7A736E] space-y-1.5">
          <li>• Click a subscriber to see their emails, engagement, payments, and referrals.</li>
          <li>• Edit any drip / system email live — changes are saved immediately.</li>
          <li>• Add or check off roadmap items inline; each completion is stamped with a version label.</li>
        </ul>
      </div>
    </div>
  );
}
